import { ProductFeature } from "~/api/services/product/enums"
import { CompanyDto, SubscriptionDataDto, SubscriptionDto } from "../api/types"
import { CompanyTier } from "../api/types/company.types"
import { isFuture, parseISO } from "date-fns"

export abstract class ACL {
  public static isSubscriptionValid(
    subscription: SubscriptionDto | null,
    requiredFeatures?: ProductFeature[],
  ) {
    if (!subscription) {
      return false
    }

    const { metadata, trialEndsAt, expiresAt } = subscription

    const hasRequiredFeatures =
      (requiredFeatures ?? []).length > 0
        ? (requiredFeatures ?? []).every(feature =>
            metadata?.features?.includes(feature),
          )
        : true

    if (!hasRequiredFeatures) {
      return false
    }

    const isTrialExpired = this.isExpired(trialEndsAt)
    const isSubscriptionExpired = this.isExpired(expiresAt)
    return !isTrialExpired || !isSubscriptionExpired
  }

  public static isSubscriptionDataValid(
    subscriptionData?: SubscriptionDataDto,
    requiredFeatures?: ProductFeature[],
  ) {
    if (!subscriptionData) {
      return false
    }

    const { features, trialEndsAt, currentPeriodEndsAt } = subscriptionData

    const hasRequiredFeatures = requiredFeatures
      ? requiredFeatures.every(feature => features?.includes(feature))
      : true

    if (!hasRequiredFeatures) {
      return false
    }

    const isTrialExpired = this.isExpired(trialEndsAt)
    const isSubscriptionExpired = this.isExpired(currentPeriodEndsAt)
    return !isTrialExpired || !isSubscriptionExpired
  }

  public static isAccessGranted(
    company: CompanyDto | null,
    subscriptionData?: SubscriptionDataDto,
  ) {
    if (!company) {
      return false
    }

    const { subscription, metadata } = company

    // Free Share of Search companies have no subscription by design — without
    // this, PagePermissionGuard bounces even the report-ready email's deep
    // link (/market-share/share-of-search) off to the connectors page. Their
    // pre-notification experience is already scoped by the Auth routing
    // (SosOnboarding); proper nav gating/paywall for the post-notification
    // state lands with APP-453.
    if (company.tier === CompanyTier.SOS_FREE) {
      return true
    }

    // STALE (paywalled) companies never get access, whatever their stored
    // subscription rows say — the DB rows can look date-valid long after the
    // company stopped paying, and the API refuses these sessions anyway.
    if (company.tier === CompanyTier.STALE) {
      return false
    }

    const accessEndsAt = metadata?.freeAccessEndsAt
    const isFreeAccessGranted = accessEndsAt && !this.isExpired(accessEndsAt)
    const isSubscriptionValid =
      this.isSubscriptionValid(subscription) ||
      this.isSubscriptionDataValid(subscriptionData)

    return isFreeAccessGranted || isSubscriptionValid
  }

  private static isExpired(date: string) {
    return !isFuture(parseISO(date))
  }
}
